import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { PostHogProvider } from "@/lib/posthog-client";
import { useEffect } from "react";
import { AppNotFound } from "../components/app-not-found";
import { api } from "../lib/api";
import appCss from "../styles.css?url";

interface MyRouterContext {
  queryClient: QueryClient;
}

const APP_URL = "https://web.trotroways.com";
const MARKETING_URL = "https://trotroways.com";
const OG_IMAGE = `${MARKETING_URL}/og-image-app.png`; // 1200×630 — app screenshot
const TWITTER_HANDLE = "@trotroways";

// ─── JSON-LD: WebApplication ──────────────────────

const webAppSchema = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name: "TrotroWays Route Planner",
  description:
    "Plan trotro, bus, and okada trips across Accra in real time. Interactive map of informal transit routes in Ghana.",
  url: APP_URL,
  applicationCategory: "TravelApplication",
  operatingSystem: "Web",
  browserRequirements: "Requires JavaScript. Works on all modern browsers.",
  inLanguage: "en",
  isAccessibleForFree: true,
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "GHS",
  },
  creator: {
    "@type": "Organization",
    name: "TrotroWays",
    url: MARKETING_URL,
  },
  featureList: [
    "Interactive trotro route map",
    "Door-to-door route planning",
    "Bus and okada routes Accra",
    "Fares and journey time estimates",
    "Offline route support",
  ],
  screenshot: OG_IMAGE,
  areaServed: [
    { "@type": "City", name: "Accra" },
    { "@type": "Country", name: "Ghana" },
  ],
};

export const Route = createRootRouteWithContext<MyRouterContext>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      // Index the app — it's a genuinely useful tool Google can rank
      { name: "robots", content: "index, follow" },

      // ── 2. Primary SEO ──────────────────────────
      // Title: tool-first framing, keyword-rich
      {
        title: "TrotroWays — Plan Trotro & Bus Routes in Accra | Live Map",
      },
      {
        name: "description",
        content:
          "Interactive map of trotro, bus, and okada routes across Accra. Enter your start and end point — get the cheapest way to get there in seconds.",
      },
      { name: "author", content: "TrotroWays" },
      {
        name: "keywords",
        content:
          "trotro route planner, accra transit map, bus routes accra, okada routes, ghana public transport planner, trotro app online",
      },

      // ── 3. Open Graph ───────────────────────────
      { property: "og:site_name", content: "TrotroWays" },
      { property: "og:type", content: "website" },
      { property: "og:url", content: APP_URL },
      {
        property: "og:title",
        content: "TrotroWays — Plan Your Trotro Route in Accra",
      },
      {
        property: "og:description",
        content:
          "Free interactive map of trotro, bus, and okada routes across Accra. Find your route and fare in seconds.",
      },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      {
        property: "og:image:alt",
        content: "TrotroWays live map showing trotro routes in Accra",
      },
      { property: "og:locale", content: "en_GH" },

      // ── 4. Twitter / X Card ─────────────────────
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: TWITTER_HANDLE },
      {
        name: "twitter:title",
        content: "TrotroWays — Plan Your Trotro Route in Accra",
      },
      {
        name: "twitter:description",
        content:
          "Free live map for trotro, bus & okada routes in Accra. Find your cheapest route now.",
      },
      { name: "twitter:image", content: OG_IMAGE },
      {
        name: "twitter:image:alt",
        content: "TrotroWays interactive transit map of Accra",
      },

      // ── 5. Geo signals ──────────────────────────
      { name: "geo.region", content: "GH-AA" },
      { name: "geo.placename", content: "Accra, Ghana" },
      { name: "geo.position", content: "5.6037;-0.1870" },
      { name: "ICBM", content: "5.6037, -0.1870" },

      // ── 6. PWA / mobile ─────────────────────────
      // Critical for map tools: tell iOS/Android this is app-like
      { name: "theme-color", content: "#1a1a1a" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      {
        name: "apple-mobile-web-app-status-bar-style",
        content: "black-translucent",
      },
      { name: "apple-mobile-web-app-title", content: "TrotroWays" },
      { name: "application-name", content: "TrotroWays" },
      { name: "msapplication-TileColor", content: "#1a1a1a" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },

      // Canonical points to the app root
      { rel: "canonical", href: APP_URL },

      // Cross-reference the marketing site (helps Google link both properties)
      { rel: "home", href: MARKETING_URL },

      // Favicons — same set as marketing site
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
      {
        rel: "icon",
        type: "image/png",
        sizes: "16x16",
        href: "/favicon-16x16.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: "/favicon-32x32.png",
      },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/apple-touch-icon.png",
      },
      { rel: "manifest", href: "/site.webmanifest" },

      // Preconnect to critical third-party origins (~300ms LCP savings each)
      { rel: "preconnect", href: "https://api.trotroways.com" },
      { rel: "preconnect", href: "https://tiles.openfreemap.org" },
      {
        rel: "preconnect",
        href: "https://o4506033501569024.ingest.us.sentry.io",
      },
    ],
  }),
  notFoundComponent: AppNotFound,

  shellComponent: RootDocument,
});

function RootDocument({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    api.createSession().catch(console.error);
  }, []);

  const bodyContent = (
    <PostHogProvider>
      {children}
    </PostHogProvider>
  );

  return (
    <html lang="en" className="h-full dark">
      <head>
        <HeadContent />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(webAppSchema) }}
        />
      </head>
      <body className="h-full m-0 overflow-hidden">
        {bodyContent}
        <Scripts />
      </body>
    </html>
  );
}
